import React, { createContext, ReactNode, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { io, Socket } from 'socket.io-client';
import { AGENT_STATUS_URL } from '~/config/config';
import { getToken, electron, truncate, log } from '~/helpers';
import { MessageTypeEnum } from '~/enums/conversations';
import * as actions from '~/store/actions';
import * as actionTypes from '~/store/actions/actionTypes';
import {
  fetchConversationById,
  getAllConversations,
} from '~/store/actions/conversation';

interface AgentStatusProviderProps {
  children: ReactNode;
}

interface IAgentStatusContext {
  connectAgentStatusWS: (token: string) => void;
  disconnectAgentStatusWS: () => void;
  refreshAgents: () => void;
  restartAgentStatusWS: () => void;
}

export const AgentStatusContext = createContext<IAgentStatusContext>(null!);

export default function AgentStatusProvider({
  children,
}: AgentStatusProviderProps) {
  // REFS
  const socket = useRef<Socket | null>(null);

  // HOOK's
  const dispatch = useDispatch();

  const getIncomingMessageNotification = (message: any) => {
    const contentType = message?.message_type;
    const content = message?.content as string;

    if (contentType === MessageTypeEnum.TEXT) {
      return { title: 'New message from Nuacom', body: truncate(content, 20) };
    }

    if (
      contentType === MessageTypeEnum.IMAGE ||
      contentType === MessageTypeEnum.STICKER
    ) {
      return { title: 'New image from Nuacom', body: '📷 Photo' };
    }

    if (contentType === MessageTypeEnum.VIDEO) {
      return { title: 'New video from Nuacom', body: '🎥 Video' };
    }

    if (contentType === MessageTypeEnum.AUDIO) {
      return { title: 'New audio from Nuacom', body: '🎤 Audio' };
    }

    if (contentType === MessageTypeEnum.CONTACT) {
      return { title: 'New contact from Nuacom', body: '📇 Contact' };
    }

    if (contentType === MessageTypeEnum.FILE) {
      if (message?.metadata?.media?.[0]?.content_type?.includes('video')) {
        return { title: 'New video from Nuacom', body: '🎥 Video' };
      }

      return { title: 'New document from Nuacom', body: '📄 Document' };
    }

    return null;
  };

  const listenEvents = () => {
    if (!socket.current) return;

    socket.current.on(
      'agent_status',
      (data: { agent: string; status: string }) => {
        dispatch(actions.updateExtensions(data));
      }
    );

    // Destkop Softphone receive session revoke from the backend.
    socket.current.on('log_out', () => {
      log.warn(
        'Destkop Softphone receive session revoke from the backend via websocket.'
      );

      dispatch(actions.logoutUserAndCreateSentryReport());
    });

    socket.current.on('after_call_work_rules_changed', () => {
      dispatch(actions.fetchAfterCallRules());
    });

    // Admin's from the user portal can set the currently registered extension to Busy.
    socket.current.on('status_changed', async () => {
      dispatch(actions.setIsBusy(true));
      dispatch(actions.statusSystem(actionTypes.STATUS.BUSY));
      dispatch(actions.unregisterUA());
    });

    socket.current.on('conversation_message_notification', data => {
      const { is_part_of_conversation, message } = data.payload;

      electron.log(
        `NUACOM-LOG: [CONVERSATIONS] - New message received via WebSocket. ConversationID: ${message?.conversation_id} | ChannelType: ${message?.channel_type} | MessageType: ${message?.message_type} | MessageID: ${message?.id} | is_part_of_conversation: ${is_part_of_conversation}.`
      );

      if (is_part_of_conversation) {
        const isMessageChatViewOpen = window.location.pathname.includes(
          message.conversation_id
        );

        const isConversationListView = /\/conversations\/?$/i.test(
          window.location.pathname
        );

        if (isMessageChatViewOpen) {
          dispatch(fetchConversationById(message.conversation_id, false));
        } else if (isConversationListView) {
          dispatch(getAllConversations());
        }

        const notificationPayload = getIncomingMessageNotification(message);

        if (notificationPayload) {
          electron.displayIncomingMessage({
            conversationId: message.conversation_id,
            title: notificationPayload?.title,
            body: notificationPayload?.body,
          });
        }
      }
    });
  };

  const connectAgentStatusWS = (token: string) => {
    if (socket.current && socket.current.connected) {
      log.info(`Agent status WebSocket is already connected`);

      return;
    }

    socket.current = io(
      `wss://${AGENT_STATUS_URL}/agent_status?socket_for=agent_status&client-security-token=${token}`,
      {
        transports: ['websocket'],
        reconnectionDelay: 7000,
      }
    );

    socket.current.on('connect', () => {
      log.info(`AgentStatus WebSocket is connected`);
    });

    socket.current.on('connect_error', err => {
      log.info(
        `AgentStatus WebSocket connection error. Reason: ${err.message}`
      );
    });

    socket.current.on('disconnect', () => {
      log.info(`AgentStatus WebSocket disconnected`);
    });

    listenEvents();
  };

  const disconnectAgentStatusWS = () => {
    if (!socket.current) return;

    if (socket.current.disconnected) {
      log.info(`AgentStatus WebSocket is already disconnected.`);

      return;
    }

    socket.current.disconnect();
  };

  const restartAgentStatusWS = () => {
    if (!socket.current) return;

    const token = getToken();

    disconnectAgentStatusWS();
    socket.current.removeAllListeners();
    socket.current = null;

    if (token) {
      connectAgentStatusWS(token);
    }
  };

  const refreshAgents = () => {
    if (!socket.current) return;

    socket.current.emit('refresh');
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      connectAgentStatusWS(token);
    }

    return () => disconnectAgentStatusWS();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AgentStatusContext.Provider
      value={{
        connectAgentStatusWS,
        disconnectAgentStatusWS,
        refreshAgents,
        restartAgentStatusWS,
      }}>
      {children}
    </AgentStatusContext.Provider>
  );
}
